import {
  AdminMembersReportPageParams,
  AdminMembersReportPageStartDate,
  AdminMembersReportPageTab,
} from "@/admin/insights/member-reports/AdminMembersReportPage"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useLabels } from "@/core/context/LabelsContext"
import OrganizationMemberMultiSelect from "@/organization/member/common/OrganizationMemberMultiSelect"
import MessageCommunityMembersButton from "@/organization/people/actions/message/MessageCommunityMembersButton"
import { GlobalID } from "@/relay/RelayTypes"
import ProfileAvatarWithDetails from "@/user/common/profile-avatar-with-details/ProfileAvatarWithDetails"
import { ProfileAvatarWithDetailsFragment$key } from "@/user/common/profile-avatar-with-details/__generated__/ProfileAvatarWithDetailsFragment.graphql"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoDropdown,
  DiscoIcon,
  DiscoIconButton,
  DiscoSection,
  DiscoTooltip,
} from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoTableFilterTags from "@disco-ui/table/header/filter/DiscoTableFilterTags"
import { useQueryParamState } from "@disco-ui/tabs/DiscoQueryParamTabs"
import DiscoTabs from "@disco-ui/tabs/DiscoTabs"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { TestIDProps } from "@utils/typeUtils"

interface Props extends TestIDProps {
  startDate: AdminMembersReportPageStartDate
  setStartDate: React.Dispatch<React.SetStateAction<AdminMembersReportPageStartDate>>
}

export default function AdminMembersReportPageHeader({
  testid = "AdminMembersReportPageHeader",
  startDate,
  setStartDate,
}: Props) {
  const drawer = useGlobalDrawer("insights")
  const [queryParams, setQueryParams] = useQueryParamState<AdminMembersReportPageParams>()
  const tab = drawer.isOpen ? drawer.params.drawerTab : queryParams.tab

  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()
  const labels = useLabels()

  return (
    <>
      <DiscoSection className={classes.container}>
        <div className={classes.selectorContainer}>
          <OrganizationMemberMultiSelect
            values={queryParams.om ? [queryParams.om] : []}
            onChange={(m) => {
              const omId = m[0] ? m[0].id : null
              handleSelectedOrgMembershipId(omId)
            }}
            renderTags={(membership) => {
              return (
                <ProfileAvatarWithDetails
                  userKey={membership.member as ProfileAvatarWithDetailsFragment$key}
                  linkToProfile={false}
                  avatarSize={24}
                />
              )
            }}
            limit={1}
            disableClearable={false}
            viewerCanManage
          />
        </div>

        <DiscoTabs
          testid={`${testid}.registrationType.tabs`}
          tabVariant={"grey-track"}
          classes={{ arrowsContainer: classes.tabs }}
          className={classes.tabsContainer}
          routes={[
            {
              label: "Overview",
              onClick: () => handleSelectTab("overview"),
              active: !tab || tab === "overview",
              testid: "overview",
            },
            {
              label: labels.admin_experience.plural,
              onClick: () => handleSelectTab("products"),
              active: tab === "products",
              testid: "products",
            },
            {
              label: "Content",
              onClick: () => handleSelectTab("contents"),
              active: tab === "contents",
              testid: "contents",
            },
            {
              label: "Events",
              onClick: () => handleSelectTab("events"),
              active: tab === "events",
              testid: "events",
            },
            {
              label: "Discussions",
              onClick: () => handleSelectTab("discussions"),
              active: tab === "discussions",
              testid: "discussions",
            },
          ]}
        />

        <div className={classes.buttonsContainer}>
          {/* Date Filter Dropdown */}
          <DiscoDropdown
            testid={"AdminMembersReportPageHeader.date-range-filter-dropdown"}
            menuButton={({ onClick }) => (
              <DiscoTooltip content={"Filter"}>
                <DiscoIconButton
                  variant={"outlined"}
                  testid={
                    "AdminMembersReportPageHeader.date-range-filter-dropdown.button"
                  }
                  onClick={onClick}
                  height={32}
                  width={32}
                  disabled={!queryParams.om}
                >
                  <DiscoIcon icon={"iconsax.sort"} height={20} width={20} />
                </DiscoIconButton>
              </DiscoTooltip>
            )}
          >
            {/* All Time */}
            <DiscoDropdownItem
              title={"All Time"}
              testid={"AdminMembersReportPageHeader.date-range-filter-dropdown.all-time"}
              onClick={() => setStartDate(null)}
            />
            {/* Last 7 days */}
            <DiscoDropdownItem
              title={"Last 7 days"}
              testid={
                "AdminMembersReportPageHeader.date-range-filter-dropdown.last-7-days"
              }
              onClick={() => setStartDate("last7days")}
            />
            {/* Last 30 days */}
            <DiscoDropdownItem
              title={"Last 30 days"}
              testid={
                "AdminMembersReportPageHeader.date-range-filter-dropdown.last-30-days"
              }
              onClick={() => setStartDate("last30days")}
            />
            {/* Last 90 days */}
            <DiscoDropdownItem
              title={"Last 90 days"}
              testid={
                "AdminMembersReportPageHeader.date-range-filter-dropdown.last-90-days"
              }
              onClick={() => setStartDate("last90days")}
            />
          </DiscoDropdown>

          {/* Email Member */}
          {Boolean(
            activeOrganization.viewerIsOwnerOrAdmin ||
              activeOrganization.viewerMembership?.hasGroupLeadRole
          ) && (
            <MessageCommunityMembersButton
              sendTo={[{ organizationMembershipId: queryParams.om! }]}
            >
              {(buttonProps) => (
                <DiscoTooltip content={`Email ${labels.admin_member.singular}`}>
                  <DiscoIconButton
                    variant={"primary"}
                    testid={"AdminMembersReportPageHeader.email-member.button"}
                    height={32}
                    width={32}
                    disabled={!queryParams.om}
                    hoverColor={"white"}
                    {...buttonProps}
                  >
                    <DiscoIcon icon={"iconsax.message"} width={20} height={20} />
                  </DiscoIconButton>
                </DiscoTooltip>
              )}
            </MessageCommunityMembersButton>
          )}
        </div>
      </DiscoSection>

      <DiscoTableFilterTags isVisible={Boolean(startDate)}>
        <DiscoTag
          name={
            startDate === "last7days"
              ? "Last 7 days"
              : startDate === "last30days"
              ? "Last 30 days"
              : startDate === "last90days"
              ? "Last 90 days"
              : "All Time"
          }
          onDelete={() => {
            setStartDate(null)
          }}
        />
      </DiscoTableFilterTags>
    </>
  )

  function handleSelectedOrgMembershipId(omId: GlobalID | null) {
    if (drawer.isOpen) {
      drawer.setParams({ om: omId || undefined })
    } else {
      setQueryParams({ om: omId || undefined })
    }
  }

  function handleSelectTab(selectedTab: AdminMembersReportPageTab) {
    if (drawer.isOpen) {
      drawer.setParams({ drawerTab: selectedTab })
    } else {
      setQueryParams({ tab: selectedTab })
    }
  }
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    border: theme.palette.constants.borderSmall,
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr auto",
      gridTemplateRows: "1fr 1fr",
    },
  },
  selectorContainer: {
    width: "100%",
  },
  tabsContainer: {
    marginBottom: 0,
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    "& span": {
      textWrap: "nowrap",
      [theme.breakpoints.down("sm")]: {
        textWrap: "auto",
      },
    },

    [theme.breakpoints.down("md")]: {
      gridRowStart: 2,
      gridColumn: "1/3",
    },
  },
  buttonsContainer: {
    display: "flex",
    gap: theme.spacing(1),
  },
}))
